import { Options } from 'contexts/projetos/ProjetosContext';
import {
  EtapaProjeto,
  EtapasEnsinoInfantil,
  ProjetoType,
  ValidationFieldType,
} from 'models/projeto';
import moment from 'moment';
import languageValues from './LanguageUtils';
import { DEFAULT_VALID, DEFAULT_VALIDATION_MODEL } from './ValidationUtils';

export const etapasConfig: {
  [k in EtapaProjeto]: {
    fields: {
      [k in keyof ProjetoType]?: ValidationFieldType & {
        keyOptions?: keyof Options;
        keyValue?: string;
        keyLabel?: string | ((i: any) => string);
      };
    };
    needValidation: boolean;
    showConsultaPublica: boolean;
    showDireitoImagem: boolean;
    showCienciaCoordenador: boolean;
    showReviewButton: boolean;
  };
} = {
  [EtapaProjeto.infosIniciais]: {
    fields: {
      idEscola: {
        required: true,
        keyOptions: 'instituicao',
        keyValue: 'id',
        keyLabel: (a) => a.nome.toUpperCase(),
      },
      codigoCooperativa: {
        required: true,
        keyOptions: 'cooperativa',
        keyValue: 'codigo',
        keyLabel: (coop) => coop.nome.toUpperCase(),
      },
      cpfsAssessores: {
        required: true,
        keyOptions: 'assessores',
        keyValue: 'cpf',
        keyLabel: (a) => a.nome.toUpperCase(),
      },
      cpfsProfessores: {
        required: true,
        keyOptions: 'educadores',
        keyValue: 'cpf',
        keyLabel: (a) => a.nome.toUpperCase(),
      },
      descricaoTurma: { required: true },
      quantidadeAlunosTurma: { required: true },
      idEtapasEnsino: {
        required: true,
        keyOptions: 'etapasEducacao',
        keyValue: 'id',
        keyLabel: (a) => a.nome.toUpperCase(),
      },
      modalidades: {
        required: false,
        keyOptions: 'modalidades',
        keyValue: 'idModalidade',
        keyLabel: 'nome',
      },
      nome: { required: true },
      periodoInicial: {
        required: true,
        validation: (val, projeto) => {
          const m = moment(val, 'DD/MM/YYYY');
          if (!/\d{2}\/\d{2}\/\d{4}/.test(val as string) || !m.isValid()) {
            return DEFAULT_VALIDATION_MODEL(
              true,
              languageValues.inputs.periodoInicial.invalid
            );
          }
          if (projeto?.periodoFinal) {
            const final = moment(projeto.periodoFinal, 'DD/MM/YYYY');
            if (final.isValid() && final.isBefore(m)) {
              return DEFAULT_VALIDATION_MODEL(
                true,
                languageValues.inputs.periodoInicial.afterFinal
              );
            }
          }
          return DEFAULT_VALID;
        },
      },
      flgInfantil: {
        required: false,
      },
      direitoImagem: { required: false },
      consultaPublica: { required: false },
    },
    needValidation: false,
    showConsultaPublica: false,
    showDireitoImagem: true,
    showCienciaCoordenador: false,
    showReviewButton: false,
  },
  [EtapaProjeto.curriculo]: {
    fields: {
      justificativa: { required: true },
      consultaPublica: { required: false },
      cienciaCordenador: { required: false },
    },
    needValidation: true,
    showConsultaPublica: true,
    showDireitoImagem: false,
    showCienciaCoordenador: true,
    showReviewButton: true,
  },
  [EtapaProjeto.expedicao]: {
    fields: {
      descricaoTerritorioInvestigado: { required: true },
      descricaoPerguntaExploratoria: { required: true },
      descricaoExpedicao: { required: true },
      consultaPublica: { required: false },
    },
    needValidation: true,
    showConsultaPublica: true,
    showDireitoImagem: false,
    showCienciaCoordenador: false,
    showReviewButton: true,
  },
  [EtapaProjeto.tituloDoProjeto]: {
    fields: {
      nome: { required: true },
      consultaPublica: { required: false },
    },
    needValidation: true,
    showConsultaPublica: true,
    showDireitoImagem: false,
    showCienciaCoordenador: false,
    showReviewButton: true,
  },
  [EtapaProjeto.indices]: {
    fields: {
      descricaoIndiceInicial: { required: true },
      descricaoIndiceFormativo: { required: true },
      consultaPublica: { required: false },
    },
    needValidation: true,
    showConsultaPublica: true,
    showDireitoImagem: false,
    showCienciaCoordenador: false,
    showReviewButton: true,
  },
  [EtapaProjeto.mobilizacao]: {
    fields: {
      descricaoComponenteCurricular: { required: true },
      descricaoAtividadeDesenvolvida: { required: true },
      consultaPublica: { required: false },
    },
    needValidation: true,
    showConsultaPublica: true,
    showDireitoImagem: false,
    showCienciaCoordenador: false,
    showReviewButton: true,
  },
  [EtapaProjeto.aprendizagem]: {
    fields: {
      descricaoComunidadeAprendizagem: { required: true },
      apoiadores: { required: true },
      consultaPublica: { required: false },
    },
    needValidation: true,
    showConsultaPublica: true,
    showDireitoImagem: false,
    showCienciaCoordenador: false,
    showReviewButton: true,
  },
  [EtapaProjeto.indiceFinal]: {
    fields: {
      descricaoIndiceFinal: { required: true },
      descricaoAtividadeIntegradora: { required: true },
      idPrincipiosValores: {
        required: true,
        keyOptions: 'principiosValores',
        keyValue: 'id',
        keyLabel: (i) => i.nome.toUpperCase(),
      },
      periodoInicial: {
        required: true,
        validation: (val) => {
          const m = moment(val, 'DD/MM/YYYY');
          if (!/\d{2}\/\d{2}\/\d{4}/.test(val as string) || !m.isValid()) {
            return DEFAULT_VALIDATION_MODEL(
              true,
              languageValues.inputs.periodoInicial.invalid
            );
          }
          return DEFAULT_VALID;
        },
      },
      periodoFinal: {
        required: true,
        validation: (val, projeto) => {
          const m = moment(val, 'DD/MM/YYYY');
          if (!/\d{2}\/\d{2}\/\d{4}/.test(val as string) || !m.isValid()) {
            return DEFAULT_VALIDATION_MODEL(
              true,
              languageValues.inputs.periodoFinal.invalid
            );
          }
          if (projeto?.periodoInicial) {
            const inicial = moment(projeto.periodoInicial, 'DD/MM/YYYY');
            if (inicial.isValid() && inicial.isAfter(m)) {
              return DEFAULT_VALIDATION_MODEL(
                true,
                languageValues.inputs.periodoFinal.beforeInicial
              );
            }
          }
          return DEFAULT_VALID;
        },
      },
      descricaoMarcouEducador: { required: false },
      consultaPublica: { required: false },
    },
    needValidation: true,
    showConsultaPublica: true,
    showDireitoImagem: false,
    showCienciaCoordenador: false,
    showReviewButton: true,
  },
  [EtapaProjeto.revisao]: {
    fields: {},
    needValidation: false,
    showConsultaPublica: false,
    showDireitoImagem: false,
    showCienciaCoordenador: false,
    showReviewButton: false,
  },
};

export const getFlgInfantil = (selectedOptions: number[]) => {
  return (
    (selectedOptions.length > 0 &&
      selectedOptions.filter((id) => {
        return !Object.values(EtapasEnsinoInfantil).includes(id);
      }).length === 0) ??
    false
  );
};
